import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  InputAdornment
} from '@mui/material';
import { PhoneEnabled as PhoneIcon, Send as SendIcon } from '@mui/icons-material';
import { useNotifications } from '../contexts/NotificationContext';
import { isValidPhoneNumber } from '../utils/smsUtils';

interface SMSInviteFormProps {
  tripId: string;
  tripTitle: string;
  onInviteSent?: () => void;
}

const SMSInviteForm: React.FC<SMSInviteFormProps> = ({ tripId, tripTitle, onInviteSent }) => {
   // const { sendTripInviteViaSMS } = useNotifications();

   const [phoneNumber, setPhoneNumber] = useState('');
   const [sending, setSending] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [success, setSuccess] = useState<string | null>(null);

   const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setPhoneNumber(e.target.value);
      // Clear any existing error/success messages
      setError(null);
      setSuccess(null);
   };

   const handleSendInvite = async () => {
      try {
         setError(null);
         setSuccess(null);
         setSending(true);

         if (!phoneNumber.trim()) {
            setError('Please enter a phone number');
            setSending(false);
            return;
         }

         if (!isValidPhoneNumber(phoneNumber)) {
            setError('Please enter a valid phone number');
            setSending(false);
            return;
         }

         // const sent = await sendTripInviteViaSMS(phoneNumber, tripId, tripTitle);

         // if (sent) {
         //   setSuccess(`Invitation sent to ${phoneNumber}`);
         //   setPhoneNumber(''); // Clear the field after successful send

         //   // Call the callback if provided
         //   if (onInviteSent) {
         //     onInviteSent();
         //   }
         // } else {
         //   setError('Failed to send the invitation');
         // }
      } catch (err) {
         console.error('Error sending SMS invite:', err);
         setError('An error occurred while sending the invitation');
      } finally {
         setSending(false);
      }
   };

   return (
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
         <Typography variant='h6' sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <SendIcon sx={{ mr: 1 }} /> Invite via SMS
         </Typography>

         <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Send an SMS invitation to join your trip "{tripTitle}". The recipient will receive a
            link to join directly.
         </Typography>

         <Divider sx={{ my: 2 }} />

         {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
               {error}
            </Alert>
         )}

         {success && (
            <Alert severity='success' sx={{ mb: 2 }}>
               {success}
            </Alert>
         )}

         <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
            <TextField
               label='Phone Number'
               variant='outlined'
               fullWidth
               value={phoneNumber}
               onChange={handlePhoneChange}
               placeholder='+1 (555) 123-4567'
               disabled={sending}
               InputProps={{
                  startAdornment: (
                     <InputAdornment position='start'>
                        <PhoneIcon color='action' />
                     </InputAdornment>
                  ),
               }}
            />

            <Button
               variant='contained'
               onClick={handleSendInvite}
               disabled={sending || !phoneNumber.trim()}
               sx={{ minWidth: 120 }}
            >
               {sending ? <CircularProgress size={24} /> : 'Send Invite'}
            </Button>
         </Box>

         <Typography variant='caption' color='text.secondary'>
            Standard SMS rates may apply. The recipient will receive a text message with a link to
            join your trip.
         </Typography>
      </Paper>
   );
};

export default SMSInviteForm; 